import React, { useEffect, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import isEmpty from 'lodash/isEmpty'
import isNil from 'lodash/isNil'

import { WizardModal } from '@/components/layout/modals/WizardModal'
import { closeModal } from '@/reducers/ui/ui.redux'
import {
  createCampaign,
  updateCampaign,
  fetchCampaigns,
  fetchVoiceAgents,
} from '@/reducers/outbound/outboundCampaigns.actions'
import { fetchContactLists } from '@/reducers/outbound/outboundContactLists.actions'

import { SetupPage } from './SetupPage'
import {
  ScheduleRetryPage,
  MIN_CALLING_HOUR,
  MAX_CALLING_HOUR,
  DEFAULT_CALL_INTERVAL_HOURS,
  MIN_CALL_INTERVAL_HOURS,
} from './ScheduleRetryPage'
import { ReviewLaunchPage } from './ReviewLaunchPage'

export const validate = (values, currentPage) => {
  const errors = {}

  if (currentPage === 0) {
    if (!values.name) {
      errors.name = 'Campaign name is required'
    }
    if (!values.voice_agent_id) {
      errors.voice_agent_id = 'AI agent is required'
    }
  }

  if (currentPage === 1) {
    if (isEmpty(values.calling_days)) {
      errors.calling_days = 'At least one calling day is required'
    }
    if (!values.calling_hours_start) {
      errors.calling_hours_start = 'Start time is required'
    } else if (
      values.calling_hours_start < MIN_CALLING_HOUR ||
      values.calling_hours_start > MAX_CALLING_HOUR
    ) {
      errors.calling_hours_start = 'Start time must be between 8:00 AM and 9:00 PM'
    }
    if (!values.calling_hours_end) {
      errors.calling_hours_end = 'End time is required'
    } else if (
      values.calling_hours_end < MIN_CALLING_HOUR ||
      values.calling_hours_end > MAX_CALLING_HOUR
    ) {
      errors.calling_hours_end = 'End time must be between 8:00 AM and 9:00 PM'
    }
    if (
      values.calling_hours_start &&
      values.calling_hours_end &&
      !errors.calling_hours_start &&
      !errors.calling_hours_end &&
      values.calling_hours_start >= values.calling_hours_end
    ) {
      errors.calling_hours_end = 'End time must be after start time'
    }
    if (!values.start_date) {
      errors.start_date = 'Start date is required'
    }
    if (!values.end_date) {
      errors.end_date = 'End date is required'
    }
    if (!isNil(values.max_concurrent_calls) && values.max_concurrent_calls !== '') {
      const maxConcurrentCalls = Number(values.max_concurrent_calls)
      if (
        !Number.isFinite(maxConcurrentCalls) ||
        maxConcurrentCalls < 1 ||
        maxConcurrentCalls > 40
      ) {
        errors.max_concurrent_calls = 'Max concurrent calls must be between 1 and 40'
      }
    }
    if (isNil(values.call_interval_hours) || values.call_interval_hours === '') {
      errors.call_interval_hours = 'Interval between calls is required'
    } else {
      const callIntervalHours = Number(values.call_interval_hours)
      if (!Number.isFinite(callIntervalHours) || callIntervalHours < MIN_CALL_INTERVAL_HOURS) {
        errors.call_interval_hours = `Interval between calls must be at least ${MIN_CALL_INTERVAL_HOURS} hours`
      }
    }
  }

  return errors
}

const defaultValues = {
  name: '',
  description: '',
  voice_agent_id: '',
  contact_list_ids: [],
  calling_days: [],
  calling_hours_start: '',
  calling_hours_end: '',
  timezone: '',
  transfer_timezone: '',
  start_date: '',
  end_date: '',
  retries_no_answer: 1,
  retries_voicemail: 1,
  max_concurrent_calls: '',
  call_interval_hours: DEFAULT_CALL_INTERVAL_HOURS,
}

export const buildInitialValues = (campaign) => {
  if (!campaign) {
    return defaultValues
  }

  return {
    ...defaultValues,
    ...campaign,
    max_concurrent_calls: campaign.max_concurrent_calls ?? '',
    call_interval_hours: campaign.call_interval_hours ?? DEFAULT_CALL_INTERVAL_HOURS,
    calling_hours_start: (campaign.calling_hours_start || '').slice(0, 5),
    calling_hours_end: (campaign.calling_hours_end || '').slice(0, 5),
    contact_list_ids: (campaign.contact_lists || []).map((list) => ({
      value: list.id,
      label: list.name,
    })),
  }
}

export const CampaignWizard = ({ campaign }) => {
  const dispatch = useDispatch()
  const formikRef = useRef(null)
  const isEditing = !!campaign
  const { data } = useSelector((state) => state.outboundCampaigns)
  const { voiceAgents } = data
  const { data: contactListData } = useSelector((state) => state.outboundContactLists)
  const { contactLists } = contactListData

  useEffect(() => {
    dispatch(fetchVoiceAgents())
    dispatch(fetchContactLists())
  }, [])

  const voiceAgentOptions = voiceAgents.map((agent) => ({
    value: agent.id,
    label: agent.use_case_name,
  }))

  const contactListOptions = contactLists.map((contactList) => ({
    value: contactList.id,
    label: contactList.name,
  }))

  const initialValues = useMemo(
    () => buildInitialValues(campaign),
    [campaign, contactListOptions.length]
  )

  const pages = [
    () => (
      <SetupPage voiceAgentOptions={voiceAgentOptions} contactListOptions={contactListOptions} />
    ),
    () => <ScheduleRetryPage />,
    (values) => (
      <ReviewLaunchPage
        values={values}
        voiceAgentOptions={voiceAgentOptions}
        contactListOptions={contactListOptions}
      />
    ),
  ]

  const handlePageChange = () => {
    setTimeout(() => {
      if (formikRef.current) {
        formikRef.current.validateForm()
      }
    }, 0)
  }

  const handleClose = () => {
    dispatch(closeModal())
  }

  const handleSave = async (values) => {
    const payload = {
      ...values,
      voice_agent_id: values.voice_agent_id || null,
      contact_list_ids: values.contact_list_ids.map((option) => option.value),
      max_concurrent_calls:
        isNil(values.max_concurrent_calls) || values.max_concurrent_calls === ''
          ? null
          : Number(values.max_concurrent_calls),
      call_interval_hours: Number(values.call_interval_hours),
    }

    try {
      if (isEditing) {
        await dispatch(updateCampaign(campaign.id, payload))
      } else {
        await dispatch(createCampaign(payload))
        await dispatch(fetchCampaigns())
      }
      dispatch(closeModal())
    } catch {
      // Error toast is handled in the action
    }
  }

  return (
    <WizardModal
      title={isEditing ? 'Edit Campaign' : 'Create Campaign'}
      pages={pages}
      initialValues={initialValues}
      initialTouched={isEditing ? { name: true } : {}}
      onSave={handleSave}
      onClose={handleClose}
      onPageChange={handlePageChange}
      validate={validate}
      innerRef={formikRef}
      rightButtonSubmitLabel={isEditing ? 'Save' : 'Launch'}
      size="small"
      alwaysEnablePreviousButton
      canCloseOnDimmerClick={false}
    />
  )
}
